import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, adminService } from '../../services/api';
import { GeneratedCode } from '../../types';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import BookIcon from '@mui/icons-material/Book';
import QrCodeIcon from '@mui/icons-material/QrCode';

const CodeGeneratorPanel: React.FC = () => {
  const navigate = useNavigate();
  const [selectedBook, setSelectedBook] = useState<number>(1);
  const [quantity, setQuantity] = useState<number>(1);
  const [generatedCodes, setGeneratedCodes] = useState<GeneratedCode[]>([]);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const books = Array.from({ length: 8 }, (_, i) => i + 1);

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('dashboardData');
      localStorage.removeItem('authUser');
      navigate('/login');
    }
  };

  const generateCodes = async () => {
    // Validaciones
    if (quantity < 1) {
      setError('La cantidad debe ser al menos 1');
      return;
    }

    setIsGenerating(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/admin/generate-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          book_id: selectedBook,
          amount: quantity
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.codes) {
        setError('Error inesperado: no se recibieron códigos del servidor.');
        setGeneratedCodes([]);
        return;
      }
      
      setGeneratedCodes(data.codes);
      setSuccess(`Se generaron ${data.codes.length} códigos exitosamente para el Libro ${selectedBook}`);
    } catch (err) {
      console.error('Error al generar códigos:', err);
      const errorMessage = err instanceof Error ? err.message : 'Error al generar códigos. Inténtalo de nuevo.';
      setError(errorMessage);
      setGeneratedCodes([]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPDF = () => {
    // TODO: Implementar descarga de PDF
    console.log('Descargar PDF - Funcionalidad pendiente');
  };

  const handlePrintCodes = () => {
    // TODO: Implementar impresión
    console.log('Imprimir códigos - Funcionalidad pendiente');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-green-50 p-4">
      {/* Header */}
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 text-gray-700 hover:text-purple-600"
          >
            <ArrowBackIcon />
            <span className="font-semibold">Volver al Panel</span>
          </button>
          
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg shadow-md hover:bg-red-600 transition-colors duration-200"
          >
            <LogoutIcon />
            <span className="font-semibold">Cerrar Sesión</span>
          </button>
        </div>

        {/* Título principal */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-r from-purple-500 to-green-500 rounded-full">
              <QrCodeIcon className="text-white text-3xl" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Generador de Códigos</h1>
          <p className="text-gray-600 text-lg">Crea códigos únicos para desbloquear libros</p>
        </div>

        {/* Formulario de generación */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
            <BookIcon className="text-purple-500" />
            Generar Nuevos Códigos
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mb-6">
            {/* Selector de libro */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Seleccionar Libro
              </label>
              <select
                value={selectedBook}
                onChange={(e) => setSelectedBook(Number(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
              >
                {books.map(book => (
                  <option key={book} value={book}>
                    Libro {book}
                  </option>
                ))}
              </select>
            </div>

            {/* Cantidad */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Cantidad de Códigos
              </label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                placeholder="Ej: 10"
              />
            </div>

            {/* Botón generar */}
            <div className="flex items-end">
              <button
                onClick={generateCodes}
                disabled={isGenerating}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-green-500 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Generando...
                  </>
                ) : (
                  <>
                    <QrCodeIcon />
                    Generar Códigos
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Mensajes de error y éxito */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 font-medium">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 font-medium">{success}</p>
            </div>
          )}
        </div>

        {/* Tabla de códigos generados */}
        {generatedCodes.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800">
                Códigos Generados ({generatedCodes.length})
              </h3>
              
              <div className="flex gap-3">
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
                >
                  <DownloadIcon />
                  <span className="font-semibold">Descargar PDF</span>
                </button>
                
                <button
                  onClick={handlePrintCodes}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
                >
                  <PrintIcon />
                  <span className="font-semibold">Imprimir</span>
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-purple-500 to-green-500 text-white">
                    <th className="px-6 py-4 text-left font-bold rounded-l-lg">#</th>
                    <th className="px-6 py-4 text-left font-bold">Código</th>
                    <th className="px-6 py-4 text-left font-bold">Libro</th>
                    <th className="px-6 py-4 text-left font-bold">Fecha de Creación</th>
                    <th className="px-6 py-4 text-left font-bold rounded-r-lg">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {generatedCodes.map((code, index) => (
                    <tr 
                      key={code.id} 
                      className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <td className="px-6 py-4 font-medium text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-mono text-lg font-bold text-purple-600 bg-purple-50 px-3 py-1 rounded-lg">
                          {code.code}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          Libro {code.book_number}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {new Date(code.created_at).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          code.is_used 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {code.is_used ? 'Usado' : 'Disponible'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Estado vacío */}
        {generatedCodes.length === 0 && !isGenerating && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-gray-400 mb-4">
              <QrCodeIcon className="text-6xl mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No hay códigos generados
            </h3>
            <p className="text-gray-500">
              Genera códigos usando el formulario de arriba para verlos aquí
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeGeneratorPanel; 